import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { Mail, Phone, MapPin, Send, Zap, MessageSquare, Globe, ShieldCheck, Headphones, ChevronDown, Check } from 'lucide-react';
import gsap from 'gsap';
import { submitContactForm } from '../redux/slice/contact.slice';
import { handleEmailClick } from '../utils/emailHandler';
import { Helmet } from 'react-helmet-async';
import astronautImg from '../assets/images/cartoon.webp';
import { Link } from 'react-router-dom';

const ContactSchema = Yup.object().shape({
    name: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email address').required('Email address is required'),
    subject: Yup.string().required('Subject selection is required'),
    message: Yup.string().required('Message content is required').min(10, 'Message must be at least 10 characters long')
});

const CONTACT_CHANNELS = [
    {
        id: 'inquiry',
        label: 'Purchase Inquiry',
        shortLabel: 'PURCHASE INQUIRY',
        icon: Mail,
        color: '#00f0ff',
        tag: 'Store & Keys',
        desc: 'Inquiries about game availability, digital keys & store orders.'
    },
    {
        id: 'delivery',
        label: 'Digital Delivery Issue',
        shortLabel: 'DELIVERY ISSUE',
        icon: Zap,
        color: '#bd00ff',
        tag: 'Instant Code Help',
        desc: 'Redemption issues, key dispatch support & instant order status.'
    },
    // {
    //     id: 'refund',
    //     label: 'Refund Request',
    //     shortLabel: 'REFUND REQUEST',
    //     icon: ShieldCheck,
    //     color: '#ff007a',
    //     tag: 'Money-Back Guarantee',
    //     desc: 'Refund eligibility evaluation, claims & purchase protection.'
    // },
    {
        id: 'support',
        label: 'Technical Support',
        shortLabel: 'TECH SUPPORT',
        icon: Headphones,
        color: '#3ddc84',
        tag: 'Setup & Install',
        desc: 'Game activation guidance, platform setup & technical help.'
    },
    {
        id: 'general',
        label: 'Other',
        shortLabel: 'GENERAL INQUIRY',
        icon: Globe,
        color: '#e60012',
        tag: 'Corporate & Media',
        desc: 'Partnership opportunities, press inquiries & general feedback.'
    },
];

export default function Contact() {
    const dispatch = useDispatch();
    const [status, setStatus] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('inquiry');
    const [isSubjectDropdownOpen, setIsSubjectDropdownOpen] = useState(false);
    const subjectDropdownRef = useRef(null);

    // Close subject dropdown when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(event.target)) {
                setIsSubjectDropdownOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsSubjectDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.contact-reveal', {
                opacity: 0,
                y: 20,
                duration: 0.8,
                stagger: 0.1,
                ease: 'power2.out'
            });
        });
        return () => ctx.revert();
    }, []);

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        setStatus('sending');
        try {
            const result = await dispatch(submitContactForm(values)).unwrap();
            if (result.status === 'success') {
                setStatus('success');
                toast.success(result.message || "Message transmitted successfully! Check your email.", {
                    style: {
                        background: '#070710',
                        color: '#00f0ff',
                        border: '1px solid rgba(0, 240, 255, 0.2)',
                        fontSize: '11px',
                    }
                });
                resetForm();
                setTimeout(() => setStatus(''), 3000);
            } else {
                setStatus('error');
                toast.error(result.message || "Failed to send message", {
                    style: {
                        background: '#070710',
                        color: '#ff007a',
                        border: '1px solid rgba(255, 0, 122, 0.2)',
                        fontSize: '11px',
                    }
                });
                setTimeout(() => setStatus(''), 3000);
            }
        } catch (error) {
            console.error("Contact submission error:", error);
            setStatus('error');
            toast.error(error || "Internal network error submitting contact message", {
                style: {
                    background: '#070710',
                    color: '#ff007a',
                    border: '1px solid rgba(255, 0, 122, 0.2)',
                    fontSize: '11px',
                }
            });
            setTimeout(() => setStatus(''), 3000);
        } finally {
            setSubmitting(false);
        }
    };

    const currentChannelObj = CONTACT_CHANNELS.find(c => c.id === selectedChannel) || CONTACT_CHANNELS[0];
    const ActiveChannelIcon = currentChannelObj.icon;

    return (
        <div className="min-h-screen bg-transparent text-gray-100 pt-20 xs:pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-24 relative overflow-hidden selection:bg-cyber-blue selection:text-black">
            <Helmet>
                <title>Contact Us | CUBE GAMES</title>
                <meta name="description" content="Get in touch with the Cube Games support team for inquiries, purchases, refunds, and technical support. We respond within 2 hours." />
                <meta name="keywords" content="contact support, game refunds, technical assistance, purchase inquiries" />
                <link rel="canonical" href="https://cubegames.ca/contact" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Contact Us | CUBE GAMES" />
                <meta property="og:description" content="Get in touch with the Cube Games support team for inquiries, purchases, refunds, and technical support. We respond within 2 hours." />
                <meta property="og:url" content="https://cubegames.ca/contact" />
            </Helmet>

            {/* Dynamic Ambient Platform Lighting */}
            <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04] pointer-events-none"></div>

            {/* Dynamic Center Halo Ambient Light matched to channel color */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-15"
                style={{ backgroundColor: currentChannelObj.color }}
            ></div>

            <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px] pointer-events-none"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Hero Header */}
                <div className="contact-reveal flex flex-col items-start text-left mb-6 sm:mb-8 md600:mb-10 space-y-2">
                    <p className="text-[9px] sm:text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                        GLOBAL SUPPORT NETWORK
                    </p>
                    <h1 className="font-cyber font-semibold text-2xl xs:text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight">
                        Get In <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Touch</span>
                    </h1>
                    <div className="h-[2px] w-16 sm:w-20 animate-gradient-bg mt-2.5 sm:mt-3 rounded-full"></div>
                </div>

                {/* Support Navigation Tab Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3.5 mb-6 sm:mb-8 md:mb-10">
                    {CONTACT_CHANNELS.map((c) => {
                        const isSelected = selectedChannel === c.id;
                        const Icon = c.icon;

                        return (
                            <button
                                key={c.id}
                                onClick={() => setSelectedChannel(c.id)}
                                className={`group p-3 xs:p-3.5 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 flex flex-col items-center justify-center text-center gap-2 sm:gap-2.5 relative overflow-hidden backdrop-blur-xl cursor-pointer
                                    ${isSelected
                                        ? 'scale-[1.02] sm:scale-[1.03] shadow-2xl z-10'
                                        : 'bg-[#080814]/60 hover:bg-[#0c0d20] hover:scale-[1.01] sm:hover:scale-[1.02]'}`}
                                style={{
                                    borderWidth: '1.5px',
                                    borderStyle: 'solid',
                                    borderColor: isSelected ? c.color : 'rgba(255, 255, 255, 0.1)',
                                    backgroundColor: isSelected ? `${c.color}20` : undefined,
                                    boxShadow: isSelected
                                        ? `0 0 20px ${c.color}40, inset 0 0 12px ${c.color}18`
                                        : 'none'
                                }}
                            >
                                <div
                                    className="p-1.5 xs:p-2 rounded-lg sm:rounded-xl transition-all duration-300"
                                    style={{
                                        backgroundColor: isSelected ? `${c.color}35` : `${c.color}12`,
                                        transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                                    }}
                                >
                                    <Icon
                                        className="w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 group-hover:scale-105"
                                        style={{
                                            color: c.color,
                                        }}
                                    />
                                </div>

                                <span
                                    className={`text-[10px] sm:text-[11px] font-cyber font-bold uppercase tracking-wider transition-all duration-300 ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`}
                                    style={{
                                        textShadow: isSelected ? `0 0 10px ${c.color}80` : 'none'
                                    }}
                                >
                                    {c.shortLabel || c.label}
                                </span>

                                <span
                                    className="text-[8px] sm:text-[9px] font-mono uppercase tracking-widest px-2 sm:px-2.5 py-0.5 rounded-full border transition-all duration-300 font-semibold max-w-full truncate"
                                    style={{
                                        borderColor: isSelected ? c.color : `${c.color}30`,
                                        backgroundColor: isSelected ? `${c.color}40` : `${c.color}10`,
                                        color: isSelected ? '#ffffff' : c.color
                                    }}
                                >
                                    {c.tag}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Section Title Toolbar */}
                <div
                    className="p-3.5 xs:p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-[#080816]/90 backdrop-blur-xl mb-6 sm:mb-8 relative overflow-hidden group transition-all duration-500"
                    style={{
                        borderColor: `${currentChannelObj.color}50`,
                        borderWidth: '1.5px',
                        borderStyle: 'solid',
                        boxShadow: `0 0 20px ${currentChannelObj.color}18, inset 0 0 12px ${currentChannelObj.color}08`,
                    }}
                >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Left Side: Title & Department Status */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div
                                className="p-2.5 rounded-xl transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${currentChannelObj.color}20`, border: `1px solid ${currentChannelObj.color}40` }}
                            >
                                <ActiveChannelIcon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" style={{ color: currentChannelObj.color }} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="font-cyber font-extrabold text-sm sm:text-base md600:text-lg text-white uppercase tracking-wider">
                                    Direct {currentChannelObj.label} Portal
                                </h3>
                                <p className="text-[10px] sm:text-[11px] text-gray-400 font-mono mt-0.5 leading-relaxed">
                                    {currentChannelObj.desc}
                                </p>
                            </div>
                        </div>

                        {/* Right Side: Quick Action Contact Buttons */}
                        <div className="flex flex-wrap flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto shrink-0">
                            <Link
                                to="mailto:info@cubegames.ca"
                                onClick={(e) => handleEmailClick(e, 'info@cubegames.ca')}
                                className="justify-center px-4 py-2.5 sm:py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyber-cyan text-xs font-cyber text-gray-200 hover:text-white transition-all flex items-center gap-2 shadow-sm whitespace-nowrap shrink-0"
                            >
                                <Mail className="w-3.5 h-3.5 text-cyber-cyan shrink-0" />
                                <span className="whitespace-nowrap">info@cubegames.ca</span>
                            </Link>
                            <Link
                                to="tel:1800-9797-5422"
                                className="justify-center px-4 py-2.5 sm:py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyber-purple text-xs font-cyber text-gray-200 hover:text-white transition-all flex items-center gap-2 shadow-sm whitespace-nowrap shrink-0"
                            >
                                <Phone className="w-3.5 h-3.5 text-cyber-purple shrink-0" />
                                <span className="whitespace-nowrap">1800-9797-5422</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Custom Floating Astronaut Animation Style */}
                <style>{`
                    @keyframes astronautFloat {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-14px) rotate(1.8deg); }
                    }
                    .animate-astronaut-float {
                        animation: astronautFloat 3.8s ease-in-out infinite;
                    }
                `}</style>

                {/* Main Content Layout: Left Form & Right Animated Cartoon Astronaut */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-center lg:items-stretch">
                    {/* Left: Contact Formik Form Column */}
                    <div className="lg:col-span-7 flex flex-col w-full">
                        <div className="p-4 xs:p-5 sm:p-6 md600:p-8 rounded-xl sm:rounded-2xl bg-[#080816]/90 border border-white/10 backdrop-blur-xl shadow-2xl relative flex-1">
                            <div className="mb-5 sm:mb-6 md600:mb-8 pb-3.5 sm:pb-4 border-b border-white/10">
                                <div className="flex items-center gap-2.5 sm:gap-3">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-cyber-cyan flex-shrink-0">
                                        <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-sm xs:text-base md600:text-lg font-cyber font-bold text-white uppercase tracking-wider">Send Direct Message</h2>
                                        <p className="text-gray-400 text-[9px] sm:text-[10px] font-mono uppercase tracking-wider mt-0.5">Automated Support Routing • 256-Bit SSL Encrypted</p>
                                    </div>
                                </div>
                            </div>

                            <Formik
                                initialValues={{
                                    name: '',
                                    email: '',
                                    subject: currentChannelObj.label,
                                    message: ''
                                }}
                                enableReinitialize={true}
                                validationSchema={ContactSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ errors, touched, isSubmitting, setFieldValue, setFieldTouched, values }) => {
                                    const activeSubjectChannel = CONTACT_CHANNELS.find(c => c.label === values.subject) || currentChannelObj;
                                    const ActiveSubjectIcon = activeSubjectChannel.icon;

                                    return (
                                        <Form className="grid grid-cols-1 md600:grid-cols-2 gap-4 sm:gap-5 md600:gap-6">
                                            {/* Full Name */}
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest block ml-1">Full Name</label>
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    className={`w-full bg-[#121526]/90 border ${errors.name && touched.name ? 'border-red-500/80' : 'border-[#20253e]'} focus:border-cyber-cyan/80 text-gray-100 text-xs rounded-lg sm:rounded-xl py-2.5 sm:py-3 px-3.5 sm:px-4 outline-none transition-all placeholder:text-gray-500 font-sans`}
                                                    placeholder="Enter your name..."
                                                    style={{ outline: 'none', boxShadow: 'none' }}
                                                />
                                                {errors.name && touched.name && (
                                                    <p className="text-xs sm:text-sm text-red-400 ml-1 font-sans font-medium">{errors.name}</p>
                                                )}
                                            </div>

                                            {/* Email Address */}
                                            <div className="space-y-1.5">
                                                <label className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest block ml-1">Email Address</label>
                                                <Field
                                                    type="email"
                                                    name="email"
                                                    className={`w-full bg-[#121526]/90 border ${errors.email && touched.email ? 'border-red-500/80' : 'border-[#20253e]'} focus:border-cyber-purple/80 text-gray-100 text-xs rounded-lg sm:rounded-xl py-2.5 sm:py-3 px-3.5 sm:px-4 outline-none transition-all placeholder:text-gray-500 font-sans`}
                                                    placeholder="Enter your email address..."
                                                    style={{ outline: 'none', boxShadow: 'none' }}
                                                />
                                                {errors.email && touched.email && (
                                                    <p className="text-xs sm:text-sm text-red-400 ml-1 font-sans font-medium">{errors.email}</p>
                                                )}
                                            </div>

                                            {/* Subject Dropdown */}
                                            <div className="md600:col-span-2 space-y-1.5 relative z-20" ref={subjectDropdownRef}>
                                                <div className="flex items-center justify-between ml-1">
                                                    <label className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                                        <span
                                                            className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                                                            style={{
                                                                backgroundColor: activeSubjectChannel.color,
                                                                boxShadow: `0 0 8px ${activeSubjectChannel.color}`
                                                            }}
                                                        />
                                                        Inquiry Subject
                                                    </label>
                                                    <span
                                                        className="text-[9px] font-mono uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border transition-all duration-300"
                                                        style={{
                                                            color: activeSubjectChannel.color,
                                                            borderColor: `${activeSubjectChannel.color}40`,
                                                            backgroundColor: `${activeSubjectChannel.color}15`,
                                                        }}
                                                    >
                                                        {activeSubjectChannel.tag}
                                                    </span>
                                                </div>

                                                {/* Hidden input to keep Formik value registered */}
                                                <Field type="hidden" name="subject" value={values.subject || activeSubjectChannel.label} />

                                                {/* Cyberpunk Dropdown Trigger Button */}
                                                <button
                                                    type="button"
                                                    id="subject-dropdown-trigger"
                                                    onClick={() => setIsSubjectDropdownOpen(prev => !prev)}
                                                    className={`w-full flex items-center justify-between gap-3 px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-[#121526]/90 border transition-all duration-300 text-left outline-none cursor-pointer ${
                                                        errors.subject && touched.subject
                                                            ? 'border-red-500/80 shadow-[0_0_12px_rgba(239,68,68,0.2)]'
                                                            : isSubjectDropdownOpen
                                                                ? 'shadow-[0_0_15px_rgba(0,240,255,0.2)] bg-[#15192e]'
                                                                : 'border-[#20253e] hover:border-white/20 hover:bg-[#15192e]/80'
                                                    }`}
                                                    style={{
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        borderColor: errors.subject && touched.subject
                                                            ? 'rgba(239, 68, 68, 0.8)'
                                                            : isSubjectDropdownOpen
                                                                ? activeSubjectChannel.color
                                                                : '#20253e'
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                                                        <div
                                                            className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                                                            style={{
                                                                backgroundColor: `${activeSubjectChannel.color}20`,
                                                                border: `1px solid ${activeSubjectChannel.color}40`,
                                                                color: activeSubjectChannel.color
                                                            }}
                                                        >
                                                            <ActiveSubjectIcon className="w-3.5 h-3.5" />
                                                        </div>
                                                        <span className="font-cyber text-xs text-white uppercase tracking-wider truncate font-semibold">
                                                            {values.subject || activeSubjectChannel.label}
                                                        </span>
                                                    </div>

                                                    <ChevronDown
                                                        className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${
                                                            isSubjectDropdownOpen ? 'rotate-180' : ''
                                                        }`}
                                                        style={{ color: isSubjectDropdownOpen ? activeSubjectChannel.color : '#9ca3af' }}
                                                    />
                                                </button>

                                                {/* Floating Dropdown Options Menu */}
                                                {isSubjectDropdownOpen && (
                                                    <div className="absolute left-0 right-0 top-full mt-1.5 rounded-xl bg-[#0b0e1b]/98 border border-white/10 p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.95),0_0_30px_rgba(0,240,255,0.1)] backdrop-blur-2xl z-50 animate-in fade-in zoom-in-95 duration-150">
                                                        <div className="space-y-1">
                                                            {CONTACT_CHANNELS.map((ch) => {
                                                                const OptionIcon = ch.icon;
                                                                const isSelected = (values.subject || activeSubjectChannel.label) === ch.label;
                                                                return (
                                                                    <button
                                                                        key={ch.id}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setFieldValue('subject', ch.label);
                                                                            setFieldTouched('subject', true, false);
                                                                            setSelectedChannel(ch.id);
                                                                            setIsSubjectDropdownOpen(false);
                                                                        }}
                                                                        className={`w-full flex items-center justify-between p-2 sm:p-2.5 rounded-lg text-left transition-all duration-200 cursor-pointer group ${
                                                                            isSelected
                                                                                ? 'bg-[#151a30] text-white border'
                                                                                : 'text-gray-300 hover:text-white hover:bg-white/[0.06] border border-transparent'
                                                                        }`}
                                                                        style={
                                                                            isSelected
                                                                                ? {
                                                                                      borderColor: `${ch.color}60`,
                                                                                      boxShadow: `0 0 15px ${ch.color}20`
                                                                                  }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                                            <div
                                                                                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105"
                                                                                style={{
                                                                                    backgroundColor: `${ch.color}20`,
                                                                                    border: `1px solid ${ch.color}40`,
                                                                                    color: ch.color
                                                                                }}
                                                                            >
                                                                                <OptionIcon className="w-3.5 h-3.5" />
                                                                            </div>
                                                                            <div className="min-w-0 flex-1">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span
                                                                                        className={`font-cyber text-[11px] sm:text-xs uppercase tracking-wider ${
                                                                                            isSelected ? 'text-white font-bold' : 'text-gray-200 group-hover:text-white'
                                                                                        }`}
                                                                                    >
                                                                                        {ch.label}
                                                                                    </span>
                                                                                    <span
                                                                                        className="text-[8px] sm:text-[8.5px] font-mono uppercase tracking-widest px-1.5 py-0.2 rounded border transition-all"
                                                                                        style={{
                                                                                            borderColor: `${ch.color}35`,
                                                                                            backgroundColor: `${ch.color}10`,
                                                                                            color: ch.color
                                                                                        }}
                                                                                    >
                                                                                        {ch.tag}
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-[9.5px] sm:text-[10px] text-gray-400 font-sans truncate mt-0.5">
                                                                                    {ch.desc}
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {isSelected ? (
                                                                            <div
                                                                                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                                                                                style={{
                                                                                    backgroundColor: `${ch.color}25`,
                                                                                    color: ch.color
                                                                                }}
                                                                            >
                                                                                <Check className="w-3 h-3" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-2 h-2 rounded-full border border-gray-600 group-hover:border-gray-400 flex-shrink-0 ml-2 mr-1.5 transition-colors" />
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {errors.subject && touched.subject && (
                                                    <p className="text-xs sm:text-sm text-red-400 ml-1 font-sans font-medium">{errors.subject}</p>
                                                )}
                                            </div>

                                        {/* Message Area */}
                                        <div className="md600:col-span-2 space-y-1.5">
                                            <label className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest block ml-1">Message Details</label>
                                            <Field
                                                as="textarea"
                                                rows="4"
                                                name="message"
                                                className={`w-full bg-[#121526]/90 border ${errors.message && touched.message ? 'border-red-500/80' : 'border-[#20253e]'} focus:border-cyber-cyan/80 text-gray-100 text-xs rounded-lg sm:rounded-xl py-2.5 sm:py-3 px-3.5 sm:px-4 outline-none transition-all resize-none placeholder:text-gray-500 font-sans`}
                                                placeholder="Describe your inquiry in detail (e.g. order ID, game title)..."
                                                style={{ outline: 'none', boxShadow: 'none' }}
                                            />
                                            {errors.message && touched.message && (
                                                <p className="text-xs sm:text-sm text-red-400 ml-1 font-sans font-medium">{errors.message}</p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <div className="md600:col-span-2 pt-1 sm:pt-2 flex items-center justify-center sm:justify-end">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting || status === 'sending'}
                                                className={`cyber-btn w-full sm500:w-auto !px-8 !py-3.5 sm:!py-4 !text-[10px] sm:!text-[11px] !tracking-[0.2em] sm:!tracking-[0.3em] flex items-center justify-center gap-2 sm:gap-2.5 font-cyber uppercase ${status === 'success' ? '!bg-cyber-green !text-black' : ''}`}
                                            >
                                                {isSubmitting || status === 'sending' ? (
                                                    <>
                                                        <Zap className="w-4 h-4 animate-spin text-cyber-cyan" />
                                                        <span>TRANSMITTING MESSAGE...</span>
                                                    </>
                                                ) : status === 'success' ? (
                                                    <span>MESSAGE SENT SUCCESSFULLY!</span>
                                                ) : (
                                                    <>
                                                        <span>SEND MESSAGE</span>
                                                        <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </div>
                    </div>

                    {/* Right: Cartoon Astronaut Image (Fully Responsive Across All Screens) */}
                    <div className="contact-reveal lg:col-span-5 flex items-center justify-center w-full mt-4 lg:mt-0">
                        <div className="w-full flex items-center justify-center p-2 sm:p-4 group">
                            <img
                                src={astronautImg}
                                alt="Support Astronaut Comms"
                                className="w-44 xs:w-52 sm:w-64 md:w-72 lg:w-full max-w-[280px] xs:max-w-[320px] sm:max-w-[360px] lg:max-w-[420px] max-h-[340px] sm:max-h-[400px] lg:max-h-[400px] h-auto object-contain animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom Quick Contact & Support Guarantee Bar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-8">
                    {/* Card 1: Email Support */}
                    <Link
                        href="mailto:info@cubegames.ca"
                        onClick={(e) => handleEmailClick(e, 'info@cubegames.ca')}
                        className="group relative p-4 sm:p-5 rounded-2xl bg-[#080816]/90 backdrop-blur-xl border border-white/10 hover:border-cyber-cyan/50 hover:shadow-[0_0_25px_rgba(0,240,255,0.15)] transition-all duration-300 flex flex-col justify-center overflow-hidden cursor-pointer"
                    >
                        <div className="p-2.5 rounded-xl bg-cyber-cyan/15 border border-cyber-cyan/30 text-cyber-cyan group-hover:scale-110 transition-transform shadow-[0_0_12px_rgba(0,240,255,0.2)] w-fit mb-3">
                            <Mail className="w-5 h-5" />
                        </div>
                        <p className="text-[10px] font-cyber font-bold text-cyber-cyan uppercase tracking-wider mb-1">Email Support</p>
                        <span className="text-xs sm:text-sm font-sans font-semibold text-white group-hover:text-cyber-cyan transition-colors block">
                            info@cubegames.ca
                        </span>
                    </Link>

                    {/* Card 2: Voice Comms */}
                    <div className="group relative p-4 sm:p-5 rounded-2xl bg-[#080816]/90 backdrop-blur-xl border border-white/10 hover:border-cyber-purple/50 hover:shadow-[0_0_25px_rgba(189,0,255,0.15)] transition-all duration-300 flex flex-col justify-center overflow-hidden">
                        <div className="p-2.5 rounded-xl bg-cyber-purple/15 border border-cyber-purple/30 text-cyber-purple group-hover:scale-110 transition-transform shadow-[0_0_12px_rgba(189,0,255,0.2)] w-fit mb-3">
                            <Phone className="w-5 h-5" />
                        </div>
                        <p className="text-[10px] font-cyber font-bold text-cyber-purple uppercase tracking-wider mb-1">Phone
                        </p>
                        <div className="flex flex-col gap-1">
                            <Link
                                to="tel:1800-9797-5422"
                                className="text-xs sm:text-sm font-sans font-semibold text-white hover:text-cyber-purple transition-colors block"
                            >
                                1800-9797-5422
                            </Link>
                            <Link
                                to="tel:1800-9797-0000"
                                className="text-xs sm:text-sm font-sans font-semibold text-white hover:text-cyber-purple transition-colors block"
                            >
                                1800-9797-0000
                            </Link>
                        </div>
                    </div>

                    {/* Card 3: Global HQ Office */}
                    <Link
                        to="https://www.google.com/maps/search/?api=1&query=124+Alison+Ave+Cambridge+ON+N1R+1N4+Canada"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative p-4 sm:p-5 rounded-2xl bg-[#080816]/90 backdrop-blur-xl border border-white/10 hover:border-emerald-500/50 hover:shadow-[0_0_25px_rgba(61,220,132,0.15)] transition-all duration-300 flex flex-col justify-center overflow-hidden cursor-pointer"
                    >
                        <div className="p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 group-hover:scale-110 transition-transform shadow-[0_0_12px_rgba(61,220,132,0.2)] w-fit mb-3">
                            <MapPin className="w-5 h-5" />
                        </div>
                        <p className="text-[10px] font-cyber font-bold text-emerald-400 uppercase tracking-wider mb-1">Address</p>
                        <span className="text-xs sm:text-sm font-sans font-semibold text-white group-hover:text-emerald-400 transition-colors block leading-snug">
                            124 Alison Ave Cambridge, ON N1R 1N4 Canada
                        </span>
                    </Link>

                    {/* Card 4: Support SLA */}
                    <div className="group relative p-4 sm:p-5 rounded-2xl bg-[#080816]/90 backdrop-blur-xl border border-white/10 hover:border-pink-500/50 hover:shadow-[0_0_25px_rgba(255,0,122,0.15)] transition-all duration-300 flex flex-col justify-center overflow-hidden">
                        <div className="p-2.5 rounded-xl bg-pink-500/15 border border-pink-500/30 text-pink-400 group-hover:scale-110 transition-transform shadow-[0_0_12px_rgba(255,0,122,0.2)] w-fit mb-3">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                        <p className="text-[10px] font-cyber font-bold text-pink-400 uppercase tracking-wider mb-1">Support SLA</p>
                        <span className="text-xs sm:text-sm font-sans font-semibold text-white block leading-snug">
                            24/7 Verified Live Support
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
